import React, {
    useReducer,
    useCallback,
    useMemo,
    useEffect,
    useContext,
} from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withCookies } from 'react-cookie';
import { useMediaQuery } from 'react-responsive';

import AWSSignal from 'components/AWSSignal';
import ScrollToTop from 'components/ScrollToTop';
import Loading from 'components/bronson/Loading';
import CustomMessage from 'components/shared/CustomMessage';
import Traductor from 'components/Traductor';
import VehicleTable from 'components/shared/vehicleCard/Table';
import VehicleRow from 'components/rentingRedRac/Card';
import PurchaseForm from 'components/rentingRedRac/Purchase/Form';
import VehicleFilters from 'components/vehicleFilters/VehicleFilters';
import AdvancedFilters from 'components/vehicleFilters/AdvancedFilters';
import HeaderPageFilters from 'components/vehicleFilters/HeaderPageFilters';
import Button from 'components/bronson/Button';
import Pagination from 'components/bronson/Pagination';
import Modal from 'components/batches/Details/DetailsModal';

import Globals from '../Globals';
import {
    filterComercialProducts,
    filterDealers,
    filterOrder,
} from 'constants/Filters';
import useSocketListeners from 'hooks/useSocketListeners';
import useServerTime from 'hooks/useServerTime';
import useDidMount from 'hooks/useDidMount';
import { UserContext } from 'contexts/UserContext';
import { disableHTMLOverflow } from 'Functions';

import * as filtersActions from '../redux/actions/filters';
import * as rentingRedRacActions from '../redux/actions/rentingRedRac';
import { makeGetCountSelectedFilters } from '../redux/selectors/filters';
import RentingService from 'services/RentingService';
import usePermissions from 'hooks/usePermissions';
import { READONLY_PERMISSION } from 'constants/Permissions';

const initialRentingRedRacPageState = {
    loading: true,
    firstRender: true,
    filtersModalVisible: false,
    purchaseVisible: false,
    purchaseSuccess: false,
    selectedVehicle: null,
    paginationTotalItems: 0,
};

const rentingRedRacReducer = (state, { type, payload }) => {
    switch (type) {
        case 'UPDATE_STATUS':
            return { ...state, loading: payload, firstRender: false };
        case 'SHOW_FILTERS_MODAL':
            return {
                ...state,
                filtersModalVisible: !state.filtersModalVisible,
            };
        case 'SHOW_PURCHASE':
            disableHTMLOverflow(!state.purchaseVisible);
            return {
                ...state,
                purchaseVisible: !state.purchaseVisible,
                selectedVehicle: payload?.removeSelectedVehicle
                    ? null
                    : state.selectedVehicle,
                purchaseSuccess: payload?.removeSelectedVehicle,
            };
        case 'CLOSE_PURCHASE_SUCCESS':
            return { ...state, purchaseSuccess: false };
        case 'SELECT_VEHICLE':
            return { ...state, selectedVehicle: payload };
        case 'UPDATE_PAGINATION':
            return { ...state, paginationTotalItems: payload };
        default:
            return state;
    }
};

const RentingRedRacPage = ({
    cookies,
    vehicles,
    initialFilters,
    filters,
    filtersOptions,
    selectedFiltersCount,
    setFilters,
    setInitialFilters,
    resetFilters,
    setVehicles,
    removeVehicle,
}) => {
    const { user } = useContext(UserContext);
    const { checkPermission } = usePermissions();
    const t = useMemo(() => new Traductor(), []);
    const isMobileOrTablet = useMediaQuery({ maxWidth: 960 });
    const { serverTime } = useServerTime(true);
    const optionsAdvancedFilters = useMemo(
        () => ({
            text: isMobileOrTablet
                ? t.getTraduction('Filters')
                : t.getTraduction('AdvancedFilters') +
                  ' ' +
                  (selectedFiltersCount > 0
                      ? '(' + selectedFiltersCount + ')'
                      : ''),
            class: 'c-btn--secondary c-btn--full',
        }),
        [isMobileOrTablet, selectedFiltersCount, t]
    );
    const [
        {
            loading,
            filtersModalVisible,
            selectedVehicle,
            purchaseVisible,
            purchaseSuccess,
            firstRender,
            paginationTotalItems,
        },
        dispatch,
    ] = useReducer(rentingRedRacReducer, initialRentingRedRacPageState);
    useSocketListeners();

    const refreshFilters = useCallback(
        newFilters => {
            setFilters({
                ...newFilters,
                [filterOrder]: { ...newFilters[filterOrder], Page: 1 },
            });
        },
        [setFilters]
    );

    const onAdvancedFiltersPress = useCallback(() => {
        dispatch({ type: 'SHOW_FILTERS_MODAL' });
    }, []);

    const onPageChange = useCallback(
        Page => {
            setFilters({
                ...filters,
                [filterOrder]: {
                    ...filters[filterOrder],
                    Page,
                },
            });
        },
        [filters, setFilters]
    );

    const fetchVehicles = useCallback(async () => {
        if (firstRender) return;
        try {
            dispatch({ type: 'UPDATE_STATUS', payload: true });
            const body = {
                ...filters,
                [filterDealers]: filters[filterDealers]?.length
                    ? filters[filterDealers]
                    : filtersOptions.Dealers.map(e => e.Id),
                [filterComercialProducts]: filters[filterComercialProducts]
                    ?.length
                    ? filters[filterComercialProducts]
                    : filtersOptions.Products.map(e => e.Id),
            };

            const response = await RentingService.getVehicles(
                body,
                cookies.get(Globals.COOKIES.JWT)?.AccessToken
            );
            if (response?.body?.Result) {
                setVehicles(response.body.Result?.Vehicles);
                dispatch({
                    type: 'UPDATE_PAGINATION',
                    payload: response.body.Result?.Total,
                });
            }
        } catch (error) {
            console.log(error);
        }
        dispatch({ type: 'UPDATE_STATUS', payload: false });
    }, [
        cookies,
        filters,
        filtersOptions.Dealers,
        filtersOptions.Products,
        firstRender,
        setVehicles,
    ]);

    const fetchInitialFilters = useCallback(async () => {
        try {
            const response = await RentingService.getFilters(
                {
                    isDealer: user.data.Profile === 12,
                    idEmploye: user?.data?.Empleado?.Id || null,
                },
                cookies.get(Globals.COOKIES.JWT)?.AccessToken
            );

            if (response?.body?.Result) {
                setInitialFilters({ options: response.body.Result });
                dispatch({ type: 'UPDATE_STATUS', payload: false });
            }
        } catch (error) {
            console.log(error);
        }
    }, [
        cookies,
        setInitialFilters,
        user.data?.Empleado?.Id,
        user.data.Profile,
    ]);

    useDidMount(() => {
        fetchInitialFilters();
    }, fetchInitialFilters);

    useEffect(() => {
        fetchVehicles();
    }, [fetchVehicles, filters]);

    const printWithoutVehiclesMessage = useCallback(() => {
        if (loading || vehicles?.length) return null;

        const options = {
            title: t.getTraduction('WithoutVehicles_title'),
            body: t.getTraduction('WithoutVehicles_body'),
            icon: 'no-vehicles',
            primaryButton: {
                options: {
                    btnTypes: ['secondary'],
                    text: t.getTraduction('ResetFilters'),
                },
                onClick: resetFilters,
            },
        };

        return <CustomMessage options={options} />;
    }, [loading, vehicles, resetFilters, t]);

    const renderFilters = () => {
        if (isMobileOrTablet) {
            return (
                <div className="o-layout__item u-1/12 u-1/2@m">
                    <Button
                        options={optionsAdvancedFilters}
                        onClick={onAdvancedFiltersPress}
                    />
                </div>
            );
        } else {
            return (
                <div className="o-layout__item u-1/5 u-3/12@l">
                    <VehicleFilters
                        refreshFilters={refreshFilters}
                        updateFilters={setFilters}
                        setInitialFilters={setInitialFilters}
                        filters={filters}
                        filtersOptions={filtersOptions}
                        availableFilters={
                            filtersOptions?.availableFilters?.principal
                        }
                    />
                    <div className="o-layout">
                        <div className="o-layout__item">
                            <Button
                                options={optionsAdvancedFilters}
                                onClick={onAdvancedFiltersPress}
                            />
                        </div>
                    </div>
                </div>
            );
        }
    };

    const onSelectVehicle = useCallback(
        payload => dispatch({ type: 'SELECT_VEHICLE', payload }),
        []
    );

    const onShowPurchase = payload => {
        dispatch({ type: 'SHOW_PURCHASE', payload });
    };

    return (
        <>
            <ScrollToTop />
            <AWSSignal />
            <section
                className="o-content-section u-pt-small u-rema-full-height"
                data-testid="dealerReceptorPageTestId"
            >
                <div className="o-page-wrap o-page-wrap--xsmall">
                    <div className="o-layout">
                        <div className="o-layout__item u-1/1">
                            <p className="u-h3">
                                {t.getTraduction('RentingPurchaseOption')}
                            </p>
                        </div>
                    </div>
                    <HeaderPageFilters
                        filters={filters}
                        filtersOptions={filtersOptions}
                        initialFilters={initialFilters}
                        setFilters={setFilters}
                        resetFilters={resetFilters}
                        renderFilters={renderFilters}
                        extraContent={() => (
                            <Button
                                options={{
                                    class:
                                        'o-layout__item u-1/12 u-1/4@m u-float-right',
                                    text: t.getTraduction('Buy'),
                                    disabled:
                                        checkPermission(READONLY_PERMISSION) ||
                                        !selectedVehicle,
                                }}
                                onClick={onShowPurchase}
                            />
                        )}
                    />
                    <div className="o-layout">
                        {!isMobileOrTablet && renderFilters()}
                        <div className="o-layout__item u-4/5 u-9/12@l u-1/1@m">
                            {printWithoutVehiclesMessage()}
                            {vehicles?.length ? (
                                <VehicleTable
                                    options={{}}
                                    loading={loading}
                                    headers={[
                                        t.getTraduction('Plate'),
                                        t.getTraduction('Brand'),
                                        t.getTraduction('Model'),
                                        t.getTraduction('Expiration'),
                                        t.getTraduction('Installation'),
                                        {
                                            text: t.getTraduction('MinImport'),
                                            tooltip: t.getTraduction(
                                                'RentingMinimumImportTooltip'
                                            ),
                                        },
                                        t.getTraduction('ValidityDateFrom'),
                                        '',
                                    ]}
                                    RowComponent={VehicleRow}
                                    items={vehicles}
                                    serverTime={serverTime}
                                    onSelectVehicle={onSelectVehicle}
                                    selectedVehicle={selectedVehicle}
                                />
                            ) : null}
                            <Pagination
                                options={{
                                    currentPage: filters[filterOrder].Page,
                                    totalRecords: paginationTotalItems,
                                    pageLimit: initialFilters.initCountVehicles,
                                }}
                                onPageChanged={onPageChange}
                                isLoading={loading}
                            />
                        </div>
                    </div>
                </div>
            </section>
            {filtersModalVisible && (
                <AdvancedFilters
                    refreshFilters={refreshFilters}
                    filters={filters}
                    filtersOptions={filtersOptions}
                    updateFilters={setFilters}
                    setInitialFilters={setInitialFilters}
                    closeAdvancedFilters={onAdvancedFiltersPress}
                    countSelectedFilters={selectedFiltersCount}
                    resetFilters={resetFilters}
                    availableFilters={
                        filtersOptions?.availableFilters?.advanced
                    }
                />
            )}
            {purchaseVisible && (
                <PurchaseForm
                    onShowPurchase={onShowPurchase}
                    vehicle={selectedVehicle}
                    removeVehicle={removeVehicle}
                />
            )}
            {purchaseSuccess && (
                <Modal visible>
                    <header className="c-modal__header">
                        <button
                            className="c-modal__close-btn  js-modal-close"
                            type="button"
                            aria-label="Close modal icon"
                            onClick={() =>
                                dispatch({ type: 'CLOSE_PURCHASE_SUCCESS' })
                            }
                        />
                    </header>
                    <div className="u-p">
                        <CustomMessage
                            options={{
                                title: t.getTraduction('PurchaseMade_title'),
                                icon: 'success',
                                primaryButton: {
                                    options: {
                                        btnTypes: [''],
                                        text: t.getTraduction('Close'),
                                    },
                                    onClick: () =>
                                        dispatch({
                                            type: 'CLOSE_PURCHASE_SUCCESS',
                                        }),
                                },
                            }}
                        />
                    </div>
                </Modal>
            )}

            <Loading ariaBusy={loading} disableIcon={true} />
        </>
    );
};

const makeMapStateToProps = () => {
    const getCountSelectedFilters = makeGetCountSelectedFilters(
        Globals.APPLICATION_PATH.RENTING_RED_RAC
    );
    const mapStateToProps = (state, props) => {
        return {
            selectedFiltersCount: getCountSelectedFilters(state, props),
            filters:
                state.filters[Globals.APPLICATION_PATH.RENTING_RED_RAC]
                    .selected,
            initialFilters:
                state.filters[Globals.APPLICATION_PATH.RENTING_RED_RAC].initial,
            filtersOptions:
                state.filters[Globals.APPLICATION_PATH.RENTING_RED_RAC].options,
            vehicles: state.rentingRedRac.vehicles,
        };
    };
    return mapStateToProps;
};

const mapDispatchToProps = dispatch => ({
    ...bindActionCreators(
        { ...filtersActions, ...rentingRedRacActions },
        args => {
            //inject key with page route to actions
            return dispatch({
                ...args,
                route: Globals.APPLICATION_PATH.RENTING_RED_RAC,
            });
        }
    ),
});

export default withCookies(
    connect(makeMapStateToProps, mapDispatchToProps)(RentingRedRacPage)
);
